const pick = (source, fields) => {
  if (!source) return null;
  return Object.fromEntries(Object.entries(fields).map(([camel, snake]) => [camel, source[snake] ?? null]));
};

const vehicleFields = {
  id: 'id', make: 'make', model: 'model', color: 'color', licenseNumber: 'license_number',
  registeredState: 'registered_state', status: 'status', type: 'type', frontProfile: 'front_profile', rearProfile: 'rear_profile',
};

export const vehicleFromV2 = (vehicle) => pick(vehicle, vehicleFields);

const parkingPackFromV2 = (owned) => {
  const pack = owned.parking_pack;
  return {
    id: owned.id,
    parkingsUsed: owned.parkings_used,
    order: { status: owned.order_status },
    parkingPack: {
      ...pick(pack, {
        name: 'name', description: 'description', status: 'status', allowedParkings: 'allowed_parkings',
        allowUnlimitedParkings: 'allow_unlimited_parkings',
        allowedDailyReservationsForUnlimitedParkings: 'allowed_daily_reservations_for_unlimited_parkings',
        displayOrder: 'display_order', createdDatetime: 'created_datetime', weekdays: 'weekdays',
      }),
      facility: {
        id: pack.facility.id,
        lots: pack.facility.lots.map((lot) => ({
          applicableScheduleId: lot.applicable_schedule_id,
          parkingSchedules: lot.parking_schedules.map((s) => pick(s, {
            id: 'id', name: 'name', availableReservationTypes: 'available_reservation_types',
          })),
        })),
      },
    },
  };
};

const lotFromV2 = (lot) => lot && {
  id: lot.id,
  name: lot.name,
  parkerInstructions: lot.parker_instructions,
  facility: {
    images: (lot.facility?.images ?? []).map((image) => ({ file: image.file })),
    address: pick(lot.facility?.address, {
      address1: 'address_1', address2: 'address_2', city: 'city', state: 'state', zipCode: 'zip_code',
    }),
  },
};

const reservationFromV2 = (reservation) => ({
  ...pick(reservation, {
    id: 'id', status: 'status', type: 'type', isRental: 'is_rental', checkinDatetime: 'checkin_datetime',
    checkoutDatetime: 'checkout_datetime', cancellationDatetime: 'cancellation_datetime', updatedDatetime: 'updated_datetime',
    openingTime: 'opening_time', closingTime: 'closing_time',
  }),
  vehicle: pick(reservation.vehicle, { licenseNumber: 'license_number', make: 'make', model: 'model' }),
  lot: lotFromV2(reservation.lot),
  order: reservation.order && {
    ...pick(reservation.order, { id: 'id', amount: 'amount', finalAmount: 'final_amount', receipt: 'receipt' }),
    itemsMetadata: reservation.order.items_metadata == null || typeof reservation.order.items_metadata === 'string'
      ? reservation.order.items_metadata ?? null
      : JSON.stringify(reservation.order.items_metadata),
  },
  review: pick(reservation.review, {
    feedback: 'feedback', rating: 'rating', isRecommended: 'is_recommended', status: 'status', rewardCredited: 'reward_credited',
  }),
});

export const parkerFromV2 = ({ me, wallet, packs, vehicles, reservations }) => ({
  id: me.id,
  email: me.email,
  phone: me.phone,
  user: pick(me.user, {
    firstName: 'first_name', lastName: 'last_name', avatar: 'avatar', role: 'role', signupCode: 'signup_code',
    hasRedeemedSignupCode: 'has_redeemed_signup_code',
  }),
  wallet: wallet?.id ? { id: wallet.id, credits: wallet.credits } : null,
  freeVouchers: (wallet?.vouchers ?? []).map((v) => pick(v, {
    id: 'id', code: 'code', isRedeemed: 'is_redeemed', redeemedOnDatetime: 'redeemed_on_datetime',
  })),
  parkerParkingPacks: (packs?.parking_packs ?? []).map(parkingPackFromV2),
  parkerCreditPacks: (packs?.credit_packs ?? []).map((owned) => ({
    id: owned.id,
    purchasedDatetime: owned.purchased_datetime,
    creditPack: pick(owned.credit_pack, { id: 'id', name: 'name', credits: 'credits', price: 'price' }),
  })),
  vehicles: (vehicles ?? []).map(vehicleFromV2),
  reservations: (reservations ?? []).map(reservationFromV2),
});
